import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Flame, Clock, Globe2, Sparkles, TrendingUp, TrendingDown, Target, ShieldAlert } from "lucide-react";

const money = (v) => `${v >= 0 ? "+" : ""}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const KPI = ({ icon: Icon, label, value, sub, tone }) => (
  <div className="ws-panel ws-panel-hover p-4" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-center gap-2 mb-2">
      <div className="ws-icon-chip w-7 h-7"><Icon className="w-3.5 h-3.5" /></div>
      <span className="text-[11px] text-[color:var(--ws-muted)] uppercase tracking-[0.14em]">{label}</span>
    </div>
    <div className={`ws-heading text-[24px] font-bold leading-none ${tone === "up" ? "ws-up" : tone === "down" ? "ws-down" : "text-white"}`}>{value}</div>
    {sub && <div className="text-[11px] text-[color:var(--ws-muted)] mt-1">{sub}</div>}
  </div>
);

const AnalyticsAdvanced = ({ adv, totals }) => {
  const navigate = useNavigate();
  const { kpis, streaks, by_hour = [], sessions = [], durations = [], insights = [] } = adv || {};
  const hourMax = useMemo(() => Math.max(...by_hour.map((h) => Math.abs(h.pnl)), 0.0001), [by_hour]);
  const sessMax = useMemo(() => Math.max(...sessions.map((s) => Math.abs(s.pnl)), 0.0001), [sessions]);
  const durMax = useMemo(() => Math.max(...durations.map((d) => Math.abs(d.pnl)), 0.0001), [durations]);
  if (!adv) return null;

  const pf = kpis.profit_factor;
  const pfDisplay = pf == null ? "—" : pf >= 999 ? "∞" : pf.toFixed(2);

  const hourColor = (h) => {
    if (h.trades === 0) return "rgba(120,140,180,0.06)";
    const t = Math.min(1, Math.abs(h.pnl) / hourMax);
    const a = 0.15 + t * 0.75;
    return h.pnl >= 0 ? `rgba(56,189,248,${a})` : `rgba(248,113,113,${a})`;
  };

  const toneClass = { good: "var(--ws-cyan)", bad: "#f87171", warn: "#fbbf24", neutral: "#8496b5" };
  const toneIcon = { good: TrendingUp, bad: TrendingDown, warn: ShieldAlert, neutral: Sparkles };

  return (
    <>
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4 ws-fade-up" style={{ animationDelay: "60ms" }} data-testid="analytics-kpi-strip">
        <KPI icon={Target} label="Profit Factor" value={pfDisplay} sub={pf != null && pf >= 1 ? "profitable edge" : "below breakeven"} tone={pf != null && pf >= 1 ? "up" : "down"} />
        <KPI icon={Activity} label="Expectancy" value={money(kpis.expectancy)} sub="avg net / trade" tone={kpis.expectancy >= 0 ? "up" : "down"} />
        <KPI icon={ShieldAlert} label="Max Drawdown" value={money(-Math.abs(kpis.max_drawdown))} sub={`${kpis.max_drawdown_pct}% off peak`} tone="down" />
        <KPI icon={TrendingUp} label="Best Trade" value={money(kpis.best_trade)} tone="up" />
        <KPI icon={TrendingDown} label="Worst Trade" value={money(kpis.worst_trade)} tone="down" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        {/* Streak Tracker */}
        <div className="ws-panel p-6 ws-fade-up" style={{ animationDelay: "100ms" }} data-testid="analytics-streaks">
          <div className="flex items-center gap-2 mb-4">
            <Flame className="w-4 h-4 text-[color:var(--ws-cyan)]" />
            <h3 className="ws-heading text-[17px] font-semibold">Streak Tracker</h3>
          </div>
          <div className="ws-price-cell p-4 mb-3 text-center">
            <div className="text-[11px] text-[color:var(--ws-muted)] uppercase tracking-[0.14em] mb-1">Current Streak</div>
            <div className={`ws-heading text-[34px] font-bold leading-none ${streaks.current_type === "win" ? "ws-up" : streaks.current_type === "loss" ? "ws-down" : "text-white"}`}>
              {streaks.current}{streaks.current_type === "win" ? "W" : streaks.current_type === "loss" ? "L" : ""}
            </div>
            <div className="text-[11px] text-[color:var(--ws-muted)] mt-1">
              {streaks.current_type === "win" ? "winning trades in a row" : streaks.current_type === "loss" ? "losing trades in a row" : "no active streak"}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="ws-price-cell p-3">
              <div className="text-[10px] text-[color:var(--ws-muted)]">Longest Win</div>
              <div className="ws-heading text-[20px] font-bold ws-up">{streaks.longest_win}</div>
            </div>
            <div className="ws-price-cell p-3">
              <div className="text-[10px] text-[color:var(--ws-muted)]">Longest Loss</div>
              <div className="ws-heading text-[20px] font-bold ws-down">{streaks.longest_loss}</div>
            </div>
          </div>
        </div>

        {/* Session Performance */}
        <div className="ws-panel p-6 ws-fade-up" style={{ animationDelay: "140ms" }} data-testid="analytics-sessions">
          <div className="flex items-center gap-2 mb-4">
            <Globe2 className="w-4 h-4 text-[color:var(--ws-cyan)]" />
            <h3 className="ws-heading text-[17px] font-semibold">Session Performance <span className="text-[11px] text-[color:var(--ws-muted)] font-normal">· UTC</span></h3>
          </div>
          <div className="space-y-3">
            {sessions.map((s) => {
              const pos = s.pnl >= 0;
              return (
                <div key={s.name} className="flex items-center gap-3">
                  <span className="text-[12px] text-[color:var(--ws-text)] w-20 shrink-0">{s.name}</span>
                  <div className="flex-1 h-4 rounded bg-[rgba(120,140,180,0.1)] overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(Math.abs(s.pnl) / sessMax) * 100}%`, background: pos ? "linear-gradient(90deg,#2563eb,#38bdf8)" : "linear-gradient(90deg,#7f1d1d,#f87171)" }} />
                  </div>
                  <span className={`text-[12px] font-semibold w-16 text-right ${pos ? "ws-up" : "ws-down"}`}>{money(s.pnl)}</span>
                </div>
              );
            })}
            {sessions.every((s) => s.trades === 0) && <div className="text-[13px] text-[color:var(--ws-muted)]">No session data.</div>}
          </div>
        </div>

        {/* Trade Duration vs P&L */}
        <div className="ws-panel p-6 ws-fade-up" style={{ animationDelay: "180ms" }} data-testid="analytics-durations">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-[color:var(--ws-cyan)]" />
            <h3 className="ws-heading text-[17px] font-semibold">Duration vs P&amp;L</h3>
          </div>
          <div className="space-y-2.5">
            {durations.map((d) => {
              const pos = d.pnl >= 0;
              return (
                <div key={d.label} className="flex items-center gap-3">
                  <span className="text-[11px] text-[color:var(--ws-muted)] w-16 shrink-0">{d.label}</span>
                  <div className="flex-1 h-3.5 rounded bg-[rgba(120,140,180,0.1)] overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(Math.abs(d.pnl) / durMax) * 100}%`, background: pos ? "linear-gradient(90deg,#2563eb,#38bdf8)" : "linear-gradient(90deg,#7f1d1d,#f87171)" }} />
                  </div>
                  <span className={`text-[11px] font-semibold w-14 text-right ${pos ? "ws-up" : "ws-down"}`}>{money(d.pnl)}</span>
                  <span className="text-[10px] text-[color:var(--ws-muted)] w-8 text-right">{d.trades}t</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        {/* Hourly Heatmap */}
        <div className="ws-panel p-6 lg:col-span-2 ws-fade-up" style={{ animationDelay: "220ms" }} data-testid="analytics-hourly-heatmap">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-[color:var(--ws-cyan)]" />
            <h3 className="ws-heading text-[17px] font-semibold">Hourly Heatmap <span className="text-[11px] text-[color:var(--ws-muted)] font-normal">· P&amp;L by close hour (UTC)</span></h3>
          </div>
          <div className="grid grid-cols-12 gap-1.5">
            {by_hour.map((h) => (
              <div key={h.hour} className="group relative" title={`${String(h.hour).padStart(2, "0")}:00 · ${money(h.pnl)} · ${h.trades} trades`}>
                <div className="rounded-md aspect-square flex items-center justify-center text-[9px] font-semibold text-white/80"
                  style={{ background: hourColor(h), border: "1px solid rgba(120,140,180,0.12)" }}>
                  {h.hour}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-4 text-[11px] text-[color:var(--ws-muted)]">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "rgba(56,189,248,0.7)" }} /> Profit</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "rgba(248,113,113,0.7)" }} /> Loss</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "rgba(120,140,180,0.06)", border: "1px solid rgba(120,140,180,0.12)" }} /> No trades</span>
          </div>
        </div>

        {/* AI Pattern Insights */}
        <div className="ws-panel p-6 ws-fade-up" style={{ animationDelay: "260ms" }} data-testid="analytics-ai-insights">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[color:var(--ws-cyan)]" />
            <h3 className="ws-heading text-[17px] font-semibold">AI Pattern Insights</h3>
          </div>
          <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
            {insights.map((ins, i) => {
              const Icon = toneIcon[ins.tone] || Sparkles;
              const c = toneClass[ins.tone] || "var(--ws-cyan)";
              const confColor = ins.confidence === "High" ? "var(--ws-cyan)" : ins.confidence === "Medium" ? "#fbbf24" : "#8496b5";
              return (
                <div key={i} className="ws-price-cell p-3" style={{ borderLeft: `2px solid ${c}` }} data-testid={`ai-insight-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: c }} />
                      <span className="text-[13px] font-semibold text-white truncate">{ins.title}</span>
                    </div>
                    <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0"
                      style={{ color: confColor, background: "rgba(120,140,180,0.1)", border: `1px solid ${confColor}44` }}>
                      {ins.confidence} · n={ins.sample_size}
                    </span>
                  </div>
                  {ins.pattern && <div className="text-[11px] font-medium text-[color:var(--ws-cyan)] mb-1">{ins.pattern}</div>}
                  <div className="text-[11px] text-[color:var(--ws-muted)] leading-snug mb-2">{ins.evidence}</div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] text-[color:var(--ws-muted)]">Impact:</span>
                    <span className={`text-[11px] font-semibold ${ins.tone === "good" ? "ws-up" : ins.tone === "bad" ? "ws-down" : "text-white"}`}>{ins.impact}</span>
                  </div>
                  <div className="text-[11px] text-[color:var(--ws-text)] leading-snug flex gap-1.5">
                    <span className="text-[color:var(--ws-cyan)] shrink-0">→</span>
                    <span>{ins.recommendation}</span>
                  </div>
                  <button
                    onClick={() => navigate(`/ws-ai-coach?q=${encodeURIComponent(`Coach me on this pattern: ${ins.title} — ${ins.pattern || ""}. ${ins.evidence || ""}`)}`)}
                    className="ws-btn-ghost mt-2.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1.5"
                    data-testid={`coach-this-${i}`}
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Coach this
                  </button>
                </div>
              );
            })}
            {insights.length === 0 && <div className="text-[13px] text-[color:var(--ws-muted)]">Trade more to unlock pattern insights.</div>}
          </div>
        </div>
      </div>
    </>
  );
};

export default AnalyticsAdvanced;
